import React, { Component, PureComponent } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import axios from 'axios';
import {
  Modal,
  Form,
  Button,
  Input,
  Dropdown,
  Checkbox,
  Label,
  Segment,
  Icon,
  Message,
} from 'semantic-ui-react';
import ReactQuill from 'react-quill';
import { Link } from 'react-router';
import moment from 'moment';

import { getInfluencerFromOrganisations } from '../../utils/helper-functions';
import { authService } from '../../utils/auth-service';

const RECIPIENT_MODES = {
  all: 'all',
  workflow: 'workflow',
  posted: 'posted',
  manual: 'manual',
};

// "By posted state" toggle values. null = nothing chosen yet (no recipients),
// matching how workflow mode requires an explicit selection before it resolves.
const POSTED_STATES = {
  yes: 'yes',
  no: 'no',
};

// hasPosted can be missing/null on a discussion — read defensively and coerce
// to a real boolean so the yes/no split is exhaustive.
const hasDiscussionPosted = discussion => Boolean(_.get(discussion, 'hasPosted', false));

const buildRecipient = discussion => {
  const influencer = discussion.influencer
    || getInfluencerFromOrganisations(discussion.organisations);
  return {
    discussionId: discussion.id,
    name: discussion.name || _.get(influencer, 'name', ''),
    email: _.get(influencer, 'email', ''),
    workflowStatus: discussion.workflowStatus || '',
    // Carried so a template can build a link that differs per creator: the
    // Lambda turns them into a single *|INFLUENCER_IDENTIFIER|*, preferring
    // Instagram. Both may be empty.
    instagramId: _.get(influencer, 'instagramId', '') || '',
    tiktokId: _.get(influencer, 'tiktokId', '') || '',
  };
};

// Built once per discussion: the modal reads every recipient several times per
// render, and discussions stay the same objects while it is open.
const recipientCache = new WeakMap();
const getRecipient = discussion => {
  if (!recipientCache.has(discussion)) recipientCache.set(discussion, buildRecipient(discussion));
  return recipientCache.get(discussion);
};

// The same email, for the "already received" warning: a custom template is its
// own email; the general template is the same email only with the same subject,
// since its body is written afresh each time.
const isSameEmail = (sent, template, subject) => {
  if (!template || sent.mandrillTemplate !== template.name) return false;
  if (!template.body) return true;
  const wanted = _.trim(subject);
  return !!wanted && _.trim(sent.subject) === wanted;
};

const matchesFilter = (discussion, term) => {
  const r = getRecipient(discussion);
  return (r.name || '').toLowerCase().includes(term)
    || (r.email || '').toLowerCase().includes(term)
    || (r.workflowStatus || '').toLowerCase().includes(term);
};

const filterDiscussions = (discussions, filterTerm) => {
  const term = _.trim(filterTerm).toLowerCase();
  return term ? discussions.filter(d => matchesFilter(d, term)) : discussions;
};

// Pure so typing a subject or message does not rebuild up to 150 checkboxes.
class RecipientChecklist extends PureComponent {
  render() {
    const {
      discussions, filterTerm, selectedDiscussionIds, onToggle,
    } = this.props;
    const filtered = filterDiscussions(discussions, filterTerm);
    if (_.isEmpty(filtered)) {
      return (
        <p className="small light">
          {discussions.length === 0 ? 'No collaborations on this campaign yet.' : 'No matches for that filter.'}
        </p>
      );
    }
    return (
      <div>
        {filtered.map(d => {
          const r = getRecipient(d);
          return (
            <div key={d.id} style={{ padding: '4px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Checkbox
                disabled={!r.email}
                label={`${r.name}${r.email ? ` <${r.email}>` : ' (no email)'}`}
                checked={selectedDiscussionIds.includes(d.id)}
                onChange={() => onToggle(d.id)}
              />
              {r.workflowStatus && <Label size="tiny" basic>{r.workflowStatus}</Label>}
            </div>
          );
        })}
      </div>
    );
  }
}

RecipientChecklist.propTypes = {
  discussions: PropTypes.array.isRequired,
  filterTerm: PropTypes.string.isRequired,
  selectedDiscussionIds: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const stripHtml = html => (html || '').replace(/<(.|\n)*?>/g, '').trim();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmailFormat = e => typeof e === 'string' && EMAIL_REGEX.test(_.trim(e));

// Variables shown as clickable chips in the UI.
const VARIABLES = [
  { name: 'name', desc: "Recipient's name" },
  { name: 'campaign_name', desc: 'Campaign name' },
  { name: 'campaign_name_safe', desc: 'Campaign name as a slug, for use inside a link' },
  { name: 'influencer_identifier', desc: 'Identifier of the recipient' },
];
// Full set the Lambda actually substitutes at send time. Keep in sync with
// SUPPORTED_VARIABLES in dyzio-email-sender/lib/variables.js.
const SUPPORTED_VARIABLE_NAMES = ['name', 'campaign_name', 'campaign_name_safe', 'influencer_identifier'];
const VARIABLE_RE = /\*\|([A-Z_][A-Z0-9_]*)\|\*/g;

const tabStyle = isActive => (isActive ? {
  backgroundColor: '#fff5f8',
  color: '#ed1f7e',
  boxShadow: '0 0 0 1px #ed1f7e inset',
  fontWeight: 600,
} : null);

const RECIPIENT_MODE_OPTIONS = [
  { key: RECIPIENT_MODES.all, value: RECIPIENT_MODES.all, text: 'All collaborations' },
  { key: RECIPIENT_MODES.workflow, value: RECIPIENT_MODES.workflow, text: 'By workflow status' },
  { key: RECIPIENT_MODES.posted, value: RECIPIENT_MODES.posted, text: 'By posted state' },
  { key: RECIPIENT_MODES.manual, value: RECIPIENT_MODES.manual, text: 'Pick people' },
];

// One form row: a narrow label column, the control, and an optional link on the right.
const Row = ({ label, extra, children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 14 }}>
    <div style={{ width: 80, flexShrink: 0, paddingTop: 9, fontWeight: 600, fontSize: 13, color: '#333' }}>
      {label}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      {extra && <div style={{ textAlign: 'right', marginTop: -2, marginBottom: 4 }}>{extra}</div>}
      {children}
    </div>
  </div>
);

Row.propTypes = {
  label: PropTypes.string.isRequired,
  extra: PropTypes.node,
  children: PropTypes.node.isRequired,
};

Row.defaultProps = { extra: null };

const findUnknownVariables = (...templates) => {
  const found = new Set();
  templates.forEach(tpl => {
    if (!tpl) return;
    let match;
    VARIABLE_RE.lastIndex = 0;
    // eslint-disable-next-line no-cond-assign
    while ((match = VARIABLE_RE.exec(tpl)) !== null) {
      if (!SUPPORTED_VARIABLE_NAMES.includes(match[1].toLowerCase())) found.add(match[1]);
    }
  });
  return [...found];
};

const INITIAL_FORM_STATE = {
  recipientMode: RECIPIENT_MODES.all,
  selectedWorkflowStatuses: [],
  postedState: null,
  selectedDiscussionIds: [],
  // null = General template. Resolved against `templates` (which survives form
  // resets) when rendering/sending, so null always behaves as General.
  templateName: null,
  // null = leave each recipient's workflow status as it is.
  workflowStatus: null,
  fromName: '',
  fromEmail: '',
  replyTo: '',
  subject: '',
  // True while the subject is the one a custom template filled in, so switching
  // back to General can clear it; typing makes it the user's own.
  subjectFromTemplate: false,
  body: '',
  filterTerm: '',
  showRecipientList: false,
};

class AdHocEmailModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      ...INITIAL_FORM_STATE,
      fromName: props.defaultFromName || '',
      fromEmail: props.defaultFromEmail || '',
      replyTo: props.defaultReplyTo || '',
      // Test-send recipient. Defaults to the signed-in user but is editable so
      // a preview can be sent to any address (e.g. when testing from a shared
      // account). Deliberately NOT reset after a real send.
      testEmail: props.currentUserEmail || '',
      isSending: false,
      confirmingSend: false,
      // 'subject' | 'body' — chips insert into whichever was last focused.
      activeField: 'body',
      // Only mark a field "touched" after first blur — errors don't show while
      // the user is typing for the first time. Once touched, error visibility
      // tracks validity normally.
      touched: { fromName: false, fromEmail: false, replyTo: false },
      // { type: 'success' | 'partial' | 'error' | 'test-success', summary?, message?, sentTo? }
      lastResult: null,
      // Sender collapses to one line when the defaults already fill it; Edit opens it.
      editingSender: !(
        _.trim(props.defaultFromName)
        && isValidEmailFormat(props.defaultFromEmail)
        && isValidEmailFormat(props.defaultReplyTo)
      ),
      // Who this campaign's emails already reached; null until loaded, and
      // stays null if the check could not be made.
      sentRecipients: null,
      skipAlreadySent: true,
    };
    this._isMounted = false;
    // Synchronous lock — state updates are async, so a fast double-click
    // can fire two onClick handlers before isSending re-renders. This guard
    // sits at the top of every send handler.
    this._sending = false;
    // Callback refs (React 15 doesn't have React.createRef).
    this._quillInstance = null;
    // Last known Quill cursor — captured on selection-change so we can still
    // insert at the right spot after the chip steals focus.
    this._lastQuillSelection = null;
  }

  componentDidMount() {
    this._isMounted = true;
    this.fetchSentRecipients();
  }

  fetchSentRecipients = async () => {
    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    if (!url) return;
    try {
      const token = authService.getToken();
      const response = await axios.get(`${url}/campaigns/${this.props.campaignId}/sent`, {
        timeout: 15000,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (this._isMounted) this.setState({ sentRecipients: _.get(response, 'data.data.sent', []) });
    } catch (e) {
      // No list, no warning — the send itself is unaffected.
    }
  }

  onSkipAlreadySentToggle = (e, { checked }) => this.setState({ skipAlreadySent: checked });

  selectedTemplate = () => {
    const { templates } = this.props;
    return templates.find(t => t.name === this.state.templateName) || templates[0] || null;
  }

  // The picked recipients who already got this email, each with when they last did.
  findAlreadySent = picked => {
    const { sentRecipients, subject } = this.state;
    if (_.isEmpty(sentRecipients) || _.isEmpty(picked)) return [];
    const template = this.selectedTemplate();
    const same = sentRecipients.filter(s => isSameEmail(s, template, subject));
    if (_.isEmpty(same)) return [];
    const byDiscussion = _.groupBy(same.filter(s => s.discussionId), 'discussionId');
    const byEmail = _.groupBy(same.filter(s => s.email), 'email');
    return _.compact(picked.map(r => {
      const hits = [
        ...(byDiscussion[r.discussionId] || []),
        ...(byEmail[_.trim(r.email).toLowerCase()] || []),
      ];
      return hits.length ? { ...r, lastSentAt: _.max(_.map(hits, 'sentAt')) } : null;
    }));
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onTemplateChange = (e, { value }) => {
    const selected = this.props.templates.find(t => t.name === value);
    const isCustom = selected && !selected.body;
    this.setState(prev => ({
      templateName: value,
      // Custom templates are fully designed in Mandrill — prefill their subject
      // (still editable). General has none, so it keeps a subject the user
      // typed and drops one a template filled in.
      subject: isCustom ? (selected.subject || '') : (prev.subjectFromTemplate ? '' : prev.subject),
      subjectFromTemplate: isCustom,
    }));
  }

  // Ticking the box picks the first status, so a ticked box always has a value.
  onSetWorkflowStatusToggle = (e, { checked }) => this.setState({
    workflowStatus: checked ? this.props.settableWorkflowStatuses[0] : null,
  });

  onWorkflowStatusChange = (e, { value }) => this.setState({ workflowStatus: value });

  toggleWorkflowStatus = status => () => {
    this.setState(prev => {
      const current = prev.selectedWorkflowStatuses;
      const next = current.includes(status)
        ? current.filter(s => s !== status)
        : [...current, status];
      return { selectedWorkflowStatuses: next };
    });
  }

  setPostedState = state => () => this.setState({ postedState: state });

  toggleDiscussionId = id => {
    this.setState(prev => {
      const current = prev.selectedDiscussionIds;
      const next = current.includes(id)
        ? current.filter(d => d !== id)
        : [...current, id];
      return { selectedDiscussionIds: next };
    });
  }

  toggleSelectAll = filteredDiscussions => () => {
    const ids = filteredDiscussions.map(d => d.id);
    this.setState(prev => {
      const allSelected = ids.every(id => prev.selectedDiscussionIds.includes(id));
      const next = allSelected
        ? prev.selectedDiscussionIds.filter(id => !ids.includes(id))
        : _.uniq([...prev.selectedDiscussionIds, ...ids]);
      return { selectedDiscussionIds: next };
    });
  }

  markTouched = field => () => {
    this.setState(prev => ({ touched: { ...prev.touched, [field]: true } }));
  }

  onSubjectChange = (e, { value }) => this.setState({ subject: value, subjectFromTemplate: false });

  onRecipientModeChange = (e, { value }) => this.setState({ recipientMode: value, showRecipientList: false });

  toggleRecipientList = () => this.setState(prev => ({ showRecipientList: !prev.showRecipientList }));

  onInsertVariable = (e, { value }) => this.insertVariable(value)();

  editSender = () => this.setState({ editingSender: true });



  onSubjectFocus = () => this.setState({ activeField: 'subject' });

  onBodyFocus = () => this.setState({ activeField: 'body' });

  // Inserts into whichever field had focus most recently. Defaults to body if
  // no field has been focused yet.
  insertVariable = name => () => {
    const variable = `*|${name.toUpperCase()}|*`;
    if (this.state.activeField === 'subject') {
      this.insertVariableIntoSubject(variable);
    } else {
      this.insertVariableIntoBody(variable);
    }
  }

  // Old semantic-ui-react Input (0.74) doesn't surface the underlying <input>
  // through ref cleanly enough for cursor-aware inserts in React 15, so we
  // append at the end. Subject is a single line — usually fine.
  insertVariableIntoSubject = variable => {
    this.setState(prev => ({ subject: `${prev.subject || ''}${variable}` }));
  }

  onQuillSelectionChange = range => { this._lastQuillSelection = range; }

  insertVariableIntoBody = variable => {
    const editor = this._quillInstance && this._quillInstance.getEditor && this._quillInstance.getEditor();
    if (!editor) {
      this.setState(prev => ({ body: `${prev.body || ''}${variable}` }));
      return;
    }
    // Quill always has a trailing newline; getLength() points AFTER it, which
    // would put the insertion on a new line. Subtract 1 to land on the last
    // existing line. If the user had a real cursor (captured via
    // selection-change), prefer that.
    const live = editor.getSelection();
    const remembered = this._lastQuillSelection;
    const fallback = Math.max(0, editor.getLength() - 1);
    const range = live || remembered || { index: fallback, length: 0 };
    editor.insertText(range.index, variable, 'user');
    editor.setSelection(range.index + variable.length, 0, 'user');
    this._lastQuillSelection = { index: range.index + variable.length, length: 0 };
  }

  onFromNameChange = (e, { value }) => this.setState({ fromName: value });

  onFromEmailChange = (e, { value }) => this.setState({ fromEmail: value });

  onReplyToChange = (e, { value }) => this.setState({ replyTo: value });

  onTestEmailChange = (e, { value }) => this.setState({ testEmail: value });

  onBodyChange = body => this.setState({ body });

  onFilterChange = (e, { value }) => this.setState({ filterTerm: value });

  // Everyone the chosen mode picks, before any already-sent skipping.
  pickRecipients = () => {
    const { discussions } = this.props;
    const {
      recipientMode,
      selectedWorkflowStatuses,
      postedState,
      selectedDiscussionIds,
    } = this.state;

    let matched;
    if (recipientMode === RECIPIENT_MODES.all) {
      matched = discussions;
    } else if (recipientMode === RECIPIENT_MODES.workflow) {
      matched = discussions.filter(d => selectedWorkflowStatuses.includes(d.workflowStatus));
    } else if (recipientMode === RECIPIENT_MODES.posted) {
      if (postedState === null) return [];
      const wantPosted = postedState === POSTED_STATES.yes;
      matched = discussions.filter(d => hasDiscussionPosted(d) === wantPosted);
    } else {
      matched = discussions.filter(d => selectedDiscussionIds.includes(d.id));
    }

    // Dedupe by email (case-insensitive, whitespace-tolerant).
    return _.uniqBy(matched.map(getRecipient), r => (r.email || '').toLowerCase().trim());
  }

  // Who the email actually goes to.
  resolveRecipients = () => {
    const picked = this.pickRecipients();
    if (!this.state.skipAlreadySent) return picked;
    const already = new Set(_.map(this.findAlreadySent(picked), 'discussionId'));
    return picked.filter(r => !already.has(r.discussionId));
  }

  renderAlreadySent = () => {
    const { skipAlreadySent } = this.state;
    const already = this.findAlreadySent(this.pickRecipients());
    if (_.isEmpty(already)) return null;
    const names = already.slice(0, 5).map(r => r.name || r.email);
    const more = already.length - names.length;
    const lastSentAt = _.max(_.map(already, 'lastSentAt'));
    return (
      // `visible`: Semantic hides warning messages inside a Form otherwise.
      <Message warning visible size="small" style={{ marginTop: 0 }}>
        <Icon name="warning sign" />
        <strong>
          {already.length === 1
            ? '1 recipient has already had this email'
            : `${already.length} of these recipients have already had this email`}
        </strong>
        {lastSentAt && ` (last sent ${moment.unix(lastSentAt).format('D MMM YYYY')})`}
        : {names.join(', ')}{more > 0 && ` +${more} more`}
        <div style={{ marginTop: 8 }}>
          <Checkbox
            label={`Don't send it to them again (${already.length})`}
            checked={skipAlreadySent}
            onChange={this.onSkipAlreadySentToggle}
          />
        </div>
      </Message>
    );
  }

  postToLambda = async ({ recipients, isTest }) => {
    const { campaignId, campaignName, templates } = this.props;
    const {
      subject, body, fromName, fromEmail, replyTo, templateName, workflowStatus,
    } = this.state;
    const selected = templates.find(t => t.name === templateName) || templates[0] || null;

    // Sender identity + organisationId are NOT sent in the body — the Lambda
    // derives them from the verified JWT in the Authorization header. Anything
    // identity-related the body claimed would be ignored on the backend.
    const payload = {
      campaignId,
      campaignName,
      // null/General → Lambda renders the org wrapper around bodyHtml; a custom
      // slug → Lambda re-validates it against the org's label and ignores body.
      templateName: selected ? selected.name : null,
      fromName: _.trim(fromName) || null,
      fromEmail: _.trim(fromEmail) || null,
      replyTo: _.trim(replyTo) || null,
      subject,
      // Custom templates are self-contained — never send a stale body the user
      // may have typed before switching template.
      bodyHtml: selected && !selected.body ? '' : body,
      recipients,
      // A test email must never move anyone's status.
      workflowStatus: isTest ? null : workflowStatus,
      isTest: !!isTest,
    };

    const url = process.env.REACT_APP_AD_HOC_EMAIL_URL;
    if (!url) {
      // Lambda not deployed yet — log and pretend success so the UI is testable.
      // eslint-disable-next-line no-console
      console.log('[ad-hoc-email] (no REACT_APP_AD_HOC_EMAIL_URL set) would post', payload);
      return { data: { sendId: 'local-noop', queued: recipients.length, skipped: 0, status: 'QUEUED' } };
    }

    const token = authService.getToken();
    const response = await axios.post(`${url}/ad-hoc-email`, payload, {
      timeout: 30000,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  }

  // First click on "Send now" — open the confirmation step instead of sending immediately.
  handleSendClick = () => {
    if (this._sending || this.state.isSending) return;
    this.setState({ confirmingSend: true });
  }

  handleCancelConfirm = () => this.setState({ confirmingSend: false });

  // Actual POST happens after the user confirms.
  handleConfirmSend = async () => {
    if (this._sending || this.state.isSending) return;
    this._sending = true;
    this.setState({ isSending: true, lastResult: null });
    const recipients = this.resolveRecipients();
    try {
      const response = await this.postToLambda({ recipients, isTest: false });
      const summary = _.get(response, 'data', {});
      // Backend is async now: 200 means "accepted and queued". Per-recipient
      // outcomes show up later in the email-history modal.
      if (this._isMounted) {
        if (summary.queued > 0) {
          // Reset the form so accidental re-sends are very deliberate.
          this.setState({
            ...INITIAL_FORM_STATE,
            fromName: this.props.defaultFromName || '',
            fromEmail: this.props.defaultFromEmail || '',
            replyTo: this.props.defaultReplyTo || '',
            isSending: false,
            confirmingSend: false,
            touched: { fromName: false, fromEmail: false, replyTo: false },
            lastResult: {
              type: 'queued',
              queued: summary.queued,
              skipped: summary.skipped || 0,
            },
          });
        } else {
          // 0 queued — every recipient had an invalid email. Should be rare
          // because validation blocks empty recipient counts upstream.
          this.setState({
            isSending: false,
            confirmingSend: false,
            lastResult: { type: 'error', message: 'No valid recipients to send to.' },
          });
        }
      }
    } catch (err) {
      console.error('[ad-hoc-email] send failed', err);
      if (this._isMounted) {
        this.setState({
          isSending: false,
          confirmingSend: false,
          lastResult: { type: 'error', message: _.get(err, 'response.data.error') || err.message || 'Send failed' },
        });
      }
    } finally {
      this._sending = false;
    }
  }

  handleSendTest = async () => {
    if (this._sending || this.state.isSending) return;
    this._sending = true;
    this.setState({ isSending: true, lastResult: null });
    const { currentUserName } = this.props;
    const testEmail = _.trim(this.state.testEmail);
    const recipients = [{
      discussionId: null,
      name: currentUserName || testEmail,
      email: testEmail,
      workflowStatus: '',
    }];
    try {
      const response = await this.postToLambda({ recipients, isTest: true });
      const summary = _.get(response, 'data', {});
      if (this._isMounted) {
        const queued = summary.queued > 0;
        this.setState({
          isSending: false,
          lastResult: {
            type: queued ? 'test-queued' : 'error',
            message: queued ? null : 'Test send failed',
            sentTo: queued ? testEmail : undefined,
          },
        });
      }
    } catch (err) {
      console.error('[ad-hoc-email] test send failed', err);
      if (this._isMounted) {
        this.setState({
          isSending: false,
          lastResult: { type: 'error', message: _.get(err, 'response.data.error') || err.message || 'Test send failed' },
        });
      }
    } finally {
      this._sending = false;
    }
  }

  render() {
    const {
      campaignId, campaignName, discussions, currentUserEmail, onClose, templates, settableWorkflowStatuses,
    } = this.props;
    const {
      recipientMode,
      selectedWorkflowStatuses,
      postedState,
      selectedDiscussionIds,
      workflowStatus,
      fromName,
      fromEmail,
      replyTo,
      testEmail,
      subject,
      body,
      filterTerm,
      isSending,
      confirmingSend,
      touched,
      lastResult,
      showRecipientList,
      editingSender,
    } = this.state;

    // null falls back to the first (General) template. needsBody drives whether
    // the message editor shows: General composes a body; custom templates are
    // self-contained in Mandrill, so the body is hidden and not required.
    const selectedTemplate = this.selectedTemplate();
    const needsBody = selectedTemplate ? selectedTemplate.body : true;

    const workflowStatusCounts = _.countBy(discussions.filter(d => !!d.workflowStatus), 'workflowStatus');
    const workflowStatuses = Object.keys(workflowStatusCounts).sort();

    // Strip HTML from body so a variable split across formatting tags is still detectable.
    const unknownVariables = findUnknownVariables(subject, needsBody ? stripHtml(body) : '');

    const filteredDiscussions = filterDiscussions(discussions, filterTerm);

    const picked = this.pickRecipients();
    const recipients = this.resolveRecipients();
    const sendable = recipients.filter(r => r.email);
    const sendableCount = sendable.length;
    const noEmailCount = recipients.length - sendableCount;
    const alreadySentSkipped = picked.length - recipients.length;

    const hasSubject = !_.isEmpty(_.trim(subject));
    const hasBody = !_.isEmpty(stripHtml(body));
    const hasFromName = !_.isEmpty(_.trim(fromName));
    const hasFromEmail = isValidEmailFormat(fromEmail);
    const hasReplyTo = isValidEmailFormat(replyTo);
    const allHeadersFilled = hasFromName && hasFromEmail && hasReplyTo;
    const hasMessage = !needsBody || hasBody;
    const canSend = allHeadersFilled && hasSubject && hasMessage && sendableCount > 0;
    const stillNeeded = _.compact([
      sendableCount === 0 && 'recipients',
      !hasSubject && 'subject',
      !hasMessage && 'message',
      !hasFromName && 'from name',
      !hasFromEmail && 'from email',
      !hasReplyTo && 'reply-to',
    ]);
    const hasTestEmail = isValidEmailFormat(testEmail);
    const canSendTest = allHeadersFilled && hasSubject && hasMessage && hasTestEmail;

    const postedCount = discussions.filter(hasDiscussionPosted).length;
    const needsChoice = (recipientMode === RECIPIENT_MODES.workflow && _.isEmpty(selectedWorkflowStatuses))
      || (recipientMode === RECIPIENT_MODES.posted && postedState === null)
      || (recipientMode === RECIPIENT_MODES.manual && _.isEmpty(selectedDiscussionIds));
    const showSenderFields = editingSender || !allHeadersFilled;

    const variableMenu = (
      <Dropdown
        text="Insert variable"
        icon="angle down"
        pointing="top right"
        style={{ fontSize: 12, color: '#2185d0' }}
        options={VARIABLES.map(v => ({
          key: v.name, value: v.name, text: `*|${v.name.toUpperCase()}|*`, description: v.desc,
        }))}
        value={null}
        selectOnBlur={false}
        onChange={this.onInsertVariable}
      />
    );

    return (
      <Modal open size="large" onClose={onClose} closeOnEscape closeOnDimmerClick={false}>
        <Modal.Header style={{ padding: '12px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 17 }}>
              Send ad-hoc email
              <span className="light" style={{ fontWeight: 'normal', fontSize: 13, marginLeft: 8, color: '#888' }}>
                · {campaignName || '—'}
              </span>
            </div>
            <Icon name="close" link onClick={onClose} />
          </div>
        </Modal.Header>
        <Modal.Content scrolling>
          <Form>
            <Row label="Send to">
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Dropdown
                  selection
                  options={RECIPIENT_MODE_OPTIONS}
                  value={recipientMode}
                  onChange={this.onRecipientModeChange}
                  style={{ minWidth: 220, marginRight: 14 }}
                />
                <span style={{ fontSize: 13, color: '#444' }}>
                  {needsChoice ? (
                    <span className="light">Choose who below</span>
                  ) : (
                    <span>
                      <strong>{sendableCount} recipient{sendableCount === 1 ? '' : 's'}</strong>
                      {noEmailCount > 0 && <span style={{ color: '#a36b00' }}> · {noEmailCount} skipped (no email)</span>}
                      {alreadySentSkipped > 0 && <span style={{ color: '#a36b00' }}> · {alreadySentSkipped} skipped (already had it)</span>}
                      {sendableCount > 0 && recipientMode !== RECIPIENT_MODES.manual && (
                        <a style={{ marginLeft: 8, cursor: 'pointer' }} onClick={this.toggleRecipientList}>
                          {showRecipientList ? 'hide' : 'show'}
                        </a>
                      )}
                    </span>
                  )}
                </span>
              </div>

              {recipientMode === RECIPIENT_MODES.workflow && (
                <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap' }}>
                  {_.isEmpty(workflowStatuses) && <span className="small light">No workflow statuses on this campaign.</span>}
                  {workflowStatuses.map(status => (
                    <Checkbox
                      key={status}
                      label={`${status} (${workflowStatusCounts[status]})`}
                      checked={selectedWorkflowStatuses.includes(status)}
                      onChange={this.toggleWorkflowStatus(status)}
                      style={{ marginRight: 18, marginBottom: 6 }}
                    />
                  ))}
                </div>
              )}

              {recipientMode === RECIPIENT_MODES.posted && (
                <Button.Group basic size="small" style={{ marginTop: 10 }}>
                  <Button
                    type="button"
                    active={postedState === POSTED_STATES.yes}
                    style={tabStyle(postedState === POSTED_STATES.yes)}
                    onClick={this.setPostedState(POSTED_STATES.yes)}
                  >
                    Has posted ({postedCount})
                  </Button>
                  <Button
                    type="button"
                    active={postedState === POSTED_STATES.no}
                    style={tabStyle(postedState === POSTED_STATES.no)}
                    onClick={this.setPostedState(POSTED_STATES.no)}
                  >
                    Not posted ({discussions.length - postedCount})
                  </Button>
                </Button.Group>
              )}

              {recipientMode === RECIPIENT_MODES.manual && (
                <Segment style={{ marginTop: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <Input
                      icon="search"
                      size="small"
                      placeholder="Filter by name, email or status…"
                      value={filterTerm}
                      onChange={this.onFilterChange}
                      style={{ flex: 1, marginRight: 12 }}
                    />
                    {!_.isEmpty(filteredDiscussions) && (
                      <Checkbox
                        label={`Select all (${filteredDiscussions.length})`}
                        checked={filteredDiscussions.every(d => selectedDiscussionIds.includes(d.id))}
                        onChange={this.toggleSelectAll(filteredDiscussions)}
                      />
                    )}
                  </div>
                  <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                    <RecipientChecklist
                      discussions={discussions}
                      filterTerm={filterTerm}
                      selectedDiscussionIds={selectedDiscussionIds}
                      onToggle={this.toggleDiscussionId}
                    />
                  </div>
                </Segment>
              )}

              {showRecipientList && recipientMode !== RECIPIENT_MODES.manual && sendableCount > 0 && (
                <div style={{ marginTop: 8, maxHeight: 160, overflowY: 'auto', fontSize: 12, color: '#555' }}>
                  {sendable.map(r => (
                    <div key={r.discussionId} style={{ padding: '2px 0' }}>{r.name} &lt;{r.email}&gt;</div>
                  ))}
                </div>
              )}
            </Row>

            {this.renderAlreadySent()}

            {templates.length > 1 && (
              <Row label="Template">
                <Dropdown
                  selection
                  search
                  fluid
                  options={templates.map(t => ({ key: t.name, value: t.name, text: t.label }))}
                  value={selectedTemplate ? selectedTemplate.name : ''}
                  onChange={this.onTemplateChange}
                />
                {!needsBody && (
                  <p className="small light" style={{ marginTop: 4 }}>
                    Designed in Mandrill — only the subject is needed.
                  </p>
                )}
              </Row>
            )}

            <Row label="Subject" extra={needsBody ? null : variableMenu}>
              <Input
                fluid
                placeholder="e.g. Hi *|NAME|*, an update on *|CAMPAIGN_NAME|*"
                value={subject}
                onChange={this.onSubjectChange}
                onFocus={this.onSubjectFocus}
              />
            </Row>

            {needsBody && (
              <Row label="Message" extra={variableMenu}>
                <ReactQuill
                  ref={el => { this._quillInstance = el; }}
                  value={body}
                  onChange={this.onBodyChange}
                  onFocus={this.onBodyFocus}
                  onChangeSelection={this.onQuillSelectionChange}
                />
              </Row>
            )}

            {unknownVariables.length > 0 && (
              <Message warning visible size="tiny">
                <Icon name="warning sign" />
                Unknown variable{unknownVariables.length === 1 ? '' : 's'}{' '}
                <strong>{unknownVariables.map(t => `*|${t}|*`).join(', ')}</strong> will be sent as literal text.
              </Message>
            )}

            <Row label="From">
              {showSenderFields ? (
                <Form.Group widths="equal" style={{ marginBottom: 0 }}>
                  <Form.Field required error={touched.fromName && !!fromName && !hasFromName}>
                    <Input
                      placeholder="From name, e.g. Dyzio Team"
                      value={fromName}
                      onChange={this.onFromNameChange}
                      onBlur={this.markTouched('fromName')}
                    />
                  </Form.Field>
                  <Form.Field required error={touched.fromEmail && !!fromEmail && !hasFromEmail}>
                    <Input
                      placeholder="From email, e.g. team@dyzio.co"
                      value={fromEmail}
                      onChange={this.onFromEmailChange}
                      onBlur={this.markTouched('fromEmail')}
                    />
                  </Form.Field>
                  <Form.Field required error={touched.replyTo && !!replyTo && !hasReplyTo}>
                    <Input
                      placeholder="Reply-to email"
                      value={replyTo}
                      onChange={this.onReplyToChange}
                      onBlur={this.markTouched('replyTo')}
                    />
                  </Form.Field>
                </Form.Group>
              ) : (
                <div style={{ fontSize: 13, paddingTop: 6 }}>
                  {fromName} &lt;{fromEmail}&gt;
                  <span className="light" style={{ color: '#888' }}> · replies to {replyTo}</span>
                  <a style={{ marginLeft: 10, cursor: 'pointer' }} onClick={this.editSender}>Edit</a>
                </div>
              )}
            </Row>

            {!_.isEmpty(settableWorkflowStatuses) && (
              <Row label="">
                <div style={{ display: 'flex', alignItems: 'center', minHeight: 32 }}>
                  <Checkbox
                    label="Then set their workflow status to"
                    checked={!!workflowStatus}
                    onChange={this.onSetWorkflowStatusToggle}
                  />
                  {workflowStatus && settableWorkflowStatuses.length === 1 && (
                    <strong style={{ marginLeft: 6, color: '#ed1f7e' }}>{workflowStatus}</strong>
                  )}
                  {workflowStatus && settableWorkflowStatuses.length > 1 && (
                    // Upward: the form's bottom edge would clip a menu opening down.
                    <Dropdown
                      inline
                      upward
                      options={settableWorkflowStatuses.map(st => ({ key: st, value: st, text: st }))}
                      value={workflowStatus}
                      onChange={this.onWorkflowStatusChange}
                      style={{ marginLeft: 8, fontWeight: 600, color: '#ed1f7e' }}
                    />
                  )}
                </div>
              </Row>
            )}

            {lastResult && lastResult.type === 'queued' && (
              <Message positive size="tiny" onDismiss={() => this.setState({ lastResult: null })}>
                <Icon name="paper plane outline" />
                Queued for {lastResult.queued} recipient{lastResult.queued === 1 ? '' : 's'}.
                {lastResult.skipped > 0 && ` ${lastResult.skipped} skipped (no email).`}
                {' '}
                <Link to={`/campaign/${campaignId}/email-history`} onClick={onClose}>
                  View in email history
                </Link>
              </Message>
            )}

            {lastResult && lastResult.type === 'test-queued' && (
              <Message positive size="tiny" onDismiss={() => this.setState({ lastResult: null })}>
                <Icon name="paper plane" />
                Test email queued — should arrive at {lastResult.sentTo || currentUserEmail} shortly.
              </Message>
            )}

            {lastResult && lastResult.type === 'error' && (
              <Message negative size="tiny" onDismiss={() => this.setState({ lastResult: null })}>
                <Icon name="exclamation circle" />
                {lastResult.message || 'Send failed.'}
              </Message>
            )}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          {confirmingSend ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <div style={{ color: '#444', textAlign: 'left', fontSize: 13, lineHeight: 1.6 }}>
                <div style={{ fontSize: 14 }}>
                  <Icon name="question circle outline" />
                  Send <strong>&ldquo;{subject}&rdquo;</strong> to <strong>{sendableCount}</strong> recipient{sendableCount === 1 ? '' : 's'}?
                </div>
                <div style={{ color: '#777' }}>
                  Template: {selectedTemplate ? selectedTemplate.label : 'General template'}
                  {' · '}From: {fromName} &lt;{fromEmail}&gt;
                </div>
                {(noEmailCount > 0 || alreadySentSkipped > 0) && (
                  <div style={{ color: '#a36b00' }}>
                    Skipping {_.compact([
                      noEmailCount > 0 && `${noEmailCount} with no email`,
                      alreadySentSkipped > 0 && `${alreadySentSkipped} who already had this email`,
                    ]).join(' and ')}.
                  </div>
                )}
                {workflowStatus && (
                  <div>Their workflow status will be set to <strong>{workflowStatus}</strong>.</div>
                )}
                {unknownVariables.length > 0 && (
                  <div style={{ color: '#a36b00' }}>Unknown variables will be sent as literal text.</div>
                )}
              </div>
              <div>
                <Button onClick={this.handleCancelConfirm} disabled={isSending}>No, go back</Button>
                <Button
                  color="pink"
                  loading={isSending}
                  disabled={isSending}
                  onClick={this.handleConfirmSend}
                >
                  Yes, send now
                </Button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Input
                  type="email"
                  // The app's global input styles beat Semantic's sizes; match the buttons.
                  input={{ style: { height: 36, paddingTop: 0, paddingBottom: 0 } }}
                  placeholder="Send the test to…"
                  value={testEmail}
                  onChange={this.onTestEmailChange}
                  error={!!_.trim(testEmail) && !hasTestEmail}
                  style={{ width: 260, marginRight: 8 }}
                />
                {/* A disabled button swallows hover, so the hint sits on a wrapper. */}
                <span
                  title={canSendTest ? '' : `Still needed: ${_.compact([...stillNeeded.filter(n => n !== 'recipients'), !hasTestEmail && 'a test address']).join(', ')}`}
                  style={{ marginRight: 4 }}
                >
                  <Button
                    type="button"
                    loading={isSending}
                    disabled={isSending || !canSendTest}
                    onClick={this.handleSendTest}
                  >
                    Send test
                  </Button>
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {!canSend && !_.isEmpty(stillNeeded) && (
                  <span className="small light" title={`Still needed: ${stillNeeded.join(', ')}`} style={{ marginRight: 12, cursor: 'help' }}>
                    {stillNeeded.length === 1 ? `Add ${stillNeeded[0]}` : `${stillNeeded.length} fields left`}
                  </span>
                )}
                <Button onClick={onClose} disabled={isSending}>Cancel</Button>
                <Button
                  color="pink"
                  loading={isSending}
                  disabled={isSending || !canSend}
                  onClick={this.handleSendClick}
                >
                  Send now
                </Button>
              </div>
            </div>
          )}
        </Modal.Actions>
      </Modal>
    );
  }
}

AdHocEmailModal.propTypes = {
  campaignId: PropTypes.string.isRequired,
  campaignName: PropTypes.string,
  discussions: PropTypes.array,
  // Prefills the (editable) test-send recipient input — backend identity comes
  // from the verified JWT in the Authorization header, not from these props.
  currentUserEmail: PropTypes.string,
  currentUserName: PropTypes.string,
  // Org-level defaults from GET /ad-hoc-email/access. Prepopulate the modal's
  // sender inputs but stay user-editable.
  defaultFromName: PropTypes.string,
  defaultFromEmail: PropTypes.string,
  defaultReplyTo: PropTypes.string,
  // Preloaded by the campaign container from GET /ad-hoc-email/templates so the
  // dropdown is ready on open (no late pop-in). First entry is General.
  templates: PropTypes.array,
  // From GET /ad-hoc-email/access; empty hides the status picker.
  settableWorkflowStatuses: PropTypes.arrayOf(PropTypes.string),
  onClose: PropTypes.func.isRequired,
};

AdHocEmailModal.defaultProps = {
  campaignName: '',
  discussions: [],
  currentUserEmail: '',
  currentUserName: '',
  defaultFromName: '',
  defaultFromEmail: '',
  defaultReplyTo: '',
  templates: [],
  settableWorkflowStatuses: [],
};

export default AdHocEmailModal;



/** WEBPACK FOOTER **
 ** ./src/components/ad-hoc-email-modal/index.js
 **/